import { useEffect, useMemo, useState } from 'react'
import { Archive, ArchiveRestore, CalendarClock, GitMerge, Handshake, Plus, Split, Undo2, X } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { setPageTitle } from '../prefs.js'
import { Checkbox, Field, NumberInput, TextInput } from '../admin/form.js'
import { Modal, Select, adminMutate } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { useSaveBar } from '../shell/editing.js'
import { useGestes, useLecture } from './associations-api.js'
import { phraseSeuleUne } from '../soirees/rattachement-model.js'
import { BarreMix, COULEURS_SERIES, MiniGraphe, detailNuit } from './Courbes.js'
import { Bloc, ChoixAssociation, Chargement, Erreur, OPTIONS_KIND, PastilleKind, PastilleStatut, optionsGroupements } from './AssociationsCommun.js'
import { useMenuSoiree } from '../soirees/menu-soiree.js'
import {
  CHEMIN_LISTE, VUE_A_DECIDER, aideEntrees, cheminFiche, signalerAssociations, compte, euros, joursAvant, libelleSource, nombre, nuitCourte, nuitLongue, pourcent,
  correctifIdentite, formDe, texteLien, texteTendance,
  type FormIdentite,
  type Association, type EvenementDeNuit, type Fiche, type KindAssociation, type NuitDeLaFiche, type ReponseListe,
} from './associations-model.js'

/**
 * EXPLOITATION › ASSOCIATIONS — LA FICHE (`/exploitation/associations/:id`, 20/10/2026).
 *
 * Tout ce que Gestion sait d'une association, la conséquence d'abord : sa
 * prochaine soirée, ses chiffres et leur tendance, puis chacune de ses
 * soirées (Pretix, Shotgun, nuits marquées à la main), ce que ses nuits ont
 * demandé (agents, staff, incidents, relevés depuis le 30/07/2026) et —
 * seulement pour qui lit déjà les montants d'une nuit en Comptabilité —
 * l'argent : la valeur faciale (un REPÈRE, jamais une recette), la part de
 * l'association, ce qui reste. Sur une soirée vendue par Shotgun, la part
 * est « inconnue », jamais 0.
 *
 * L'identité, la note et le rôle du contact passent par la Save Bar ; tous
 * les autres gestes (alias, liens, fusion, scission, archivage) sont
 * immédiats, s'annulent (« ANNULER », puis l'historique en bas de page) et
 * ne sont jamais retouchés par la passe automatique.
 *
 * Données personnelles : UNE note libre et un rôle de contact, rien de plus
 * (décision de l'exploitant) — l'écran rappelle de n'y mettre ni numéro ni
 * e-mail personnel.
 */

export function AssociationFichePage({ id }: { id: string }) {
  const { etat, relire } = useLecture<Fiche>(`/api/v1/associations/${id}`)
  const nom = etat.phase === 'ok' ? etat.data.association.name : null
  // L'onglet porte le nom de l'association, une fois lue (le registre ne la connaît pas).
  useEffect(() => { if (nom) { const t = window.setTimeout(() => setPageTitle(nom), 0); return () => window.clearTimeout(t) } return undefined }, [nom])
  return (
    <main className="lc-page lc-page--management lc-stagger asso-page">
      <Trail {...(nom ? { leaf: nom } : {})} />
      <SmartBack fallback={CHEMIN_LISTE} />
      {etat.phase === 'chargement' ? <Chargement libelle="Lecture de l’association" />
        : etat.phase === 'erreur' ? <Erreur message={etat.message} {...(etat.status === 404 ? {} : { onReessayer: relire })} />
          : <Contenu f={etat.data} />}
    </main>
  )
}

function Contenu({ f }: { f: Fiche }) {
  const a = f.association
  const gerer = f.droits.gerer
  // Les autres associations : pour « membre de », « fusionner dans », « lier à une autre ».
  const { etat: liste } = useLecture<ReponseListe>(gerer ? '/api/v1/associations?statut=toutes' : null)
  const autres = liste.phase === 'ok' ? liste.data.associations : []
  const vivante = a.status === 'ACTIVE'
  return (
    <>
      <EnTete f={f} autres={autres} />
      {a.status === 'SUGGESTED' ? (
        <p className="asso-bandeau" role="status">
          Gestion suggère cette association : rien ne lui est lié tant qu’elle n’est pas confirmée.{' '}
          <Link href={`${CHEMIN_LISTE}?vue=${VUE_A_DECIDER}`}>La confirmer ou la rejeter dans « À décider »</Link>.
        </p>
      ) : null}
      {a.merged_into_id ? (
        <p className="asso-bandeau" role="status">
          Fusionnée dans <Link href={cheminFiche(a.merged_into_id)}>{a.merged_into_name ?? 'une autre association'}</Link> : ses soirées comptent là-bas.
          Pour revenir en arrière, « Annuler » la fusion dans l’historique de cette association-là.
        </p>
      ) : null}
      <ProchaineSoiree f={f} />
      <Chiffres f={f} />
      <Tendances f={f} />
      <SesSoirees f={f} autres={autres} vivante={vivante} />
      <OperationsBloc f={f} />
      <ArgentBloc f={f} />
      <Identite f={f} autres={autres} />
      <Historique f={f} />
    </>
  )
}

/* ═══════════════════════════════════════ en-tête et gestes ═══ */

function EnTete({ f, autres }: { f: Fiche; autres: Association[] }) {
  const a = f.association
  const { envoyer } = useGestes()
  const confirmer = useConfirm()
  const [fusion, setFusion] = useState(false)
  const [scission, setScission] = useState(false)
  const archiver = async () => {
    const ok = await confirmer({
      title: `Archiver « ${a.name} » ?`,
      message: 'Ses soirées passées restent les siennes, avec leurs chiffres ; Gestion ne lui en liera plus de nouvelles. Elle sort de la liste des actives. « Restaurer » la rend active.',
      confirmLabel: 'Archiver',
    })
    if (ok) await envoyer('POST', `/api/v1/associations/${a.id}/archiver`, {})
  }
  return (
    <header className="asso-hero">
      <div className="asso-hero__texte">
        <h1 className="lc-title"><Handshake size={26} aria-hidden="true" /> {a.name}</h1>
        <p className="asso-hero__pastilles">
          <PastilleKind kind={a.kind} /> <PastilleStatut statut={a.status} />
          {a.school ? <span>{a.school}</span> : null}
          {a.member_of_id ? <span>membre de <Link href={cheminFiche(a.member_of_id)}>{a.member_of_name}</Link></span> : null}
          {a.contact_role ? <span>Contact : {a.contact_role}</span> : null}
        </p>
        {f.membres.length > 0 ? (
          <p className="asso-hero__membres">
            <span className="lc-hint">Ses membres : </span>
            {f.membres.map((m, i) => <span key={m.id}>{i > 0 ? ', ' : ''}<Link href={cheminFiche(m.id)}>{m.name}</Link>{m.status === 'SUGGESTED' ? ' (suggérée)' : ''}</span>)}
          </p>
        ) : null}
      </div>
      {f.droits.gerer ? (
        <div className="asso-hero__actions">
          {a.status === 'ACTIVE' || a.status === 'SUGGESTED' ? (
            <button type="button" className="lc-btn" onClick={() => setFusion(true)}><GitMerge size={16} aria-hidden="true" /> Fusionner…</button>
          ) : null}
          {a.status === 'ACTIVE' ? (
            <>
              <button type="button" className="lc-btn" onClick={() => setScission(true)} disabled={f.nuits.length === 0}><Split size={16} aria-hidden="true" /> Scinder…</button>
              <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void archiver() }}><Archive size={16} aria-hidden="true" /> Archiver</button>
            </>
          ) : null}
          {a.status === 'ARCHIVED' && !a.merged_into_id ? (
            <button type="button" className="lc-btn" onClick={() => { void envoyer('POST', `/api/v1/associations/${a.id}/restaurer`, {}) }}>
              <ArchiveRestore size={16} aria-hidden="true" /> Restaurer
            </button>
          ) : null}
        </div>
      ) : null}
      <ModaleFusion open={fusion} onClose={() => setFusion(false)} f={f} autres={autres} />
      <ModaleScission open={scission} onClose={() => setScission(false)} f={f} />
    </header>
  )
}

function ModaleFusion({ open, onClose, f, autres }: { open: boolean; onClose: () => void; f: Fiche; autres: Association[] }) {
  const a = f.association
  const { envoyer } = useGestes()
  const [cible, setCible] = useState('')
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  // Un groupement ne se fusionne qu'avec un groupement.
  const possibles = autres.filter((x) => (x.kind === 'GROUPEMENT') === (a.kind === 'GROUPEMENT'))
  const nomCible = autres.find((x) => x.id === cible)?.name
  const fusionner = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const e = await envoyer('POST', `/api/v1/associations/${a.id}/fusionner`, { cible_id: cible, motif: motif.trim() || null })
      if (e) { setErreur(e); return }
      onClose()
      navigate(cheminFiche(cible))
    } finally { setEnvoi(false) }
  }
  return (
    <Modal open={open} onClose={onClose} ariaLabel={`Fusionner ${a.name}`} width={520}>
      <div className="asso-dialogue">
        <h3>Fusionner « {a.name} » dans une autre association</h3>
        <p className="lc-hint">
          Ses soirées, ses alias et ses nuits marquées passent à l’association choisie ; « {a.name} » est archivée, rien n’est
          supprimé. « Annuler » (le message qui suit, ou l’historique de l’autre association) défait la fusion.
        </p>
        <ChoixAssociation label="Fusionner dans" associations={possibles} value={cible} onChange={setCible} sauf={a.id} />
        <Field label="Pourquoi (facultatif)">
          <TextInput value={motif} onChange={setMotif} maxLength={300} ariaLabel="Motif de la fusion" placeholder="Même BDE, deux orthographes" />
        </Field>
        {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
        <div className="asso-dialogue__pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !cible} onClick={() => { void fusionner() }}>
            {envoi ? 'Fusion…' : nomCible ? `Fusionner dans ${nomCible}` : 'Fusionner'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ModaleScission({ open, onClose, f }: { open: boolean; onClose: () => void; f: Fiche }) {
  const a = f.association
  const { envoyer } = useGestes()
  const [nom, setNom] = useState('')
  const [kind, setKind] = useState<KindAssociation>(a.kind)
  const [evts, setEvts] = useState<string[]>([])
  const [nuits, setNuits] = useState<string[]>([])
  const [alias, setAlias] = useState<string[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const bascule = (l: string[], x: string) => (l.includes(x) ? l.filter((y) => y !== x) : [...l, x])
  // Seules SES soirées se déplacent : celles d'un membre, vues ici à travers le groupement, restent au membre.
  const propres = f.nuits.flatMap((n) => n.nuit.evenements.filter((e) => a.kind !== 'GROUPEMENT' || e.lien.group_id !== a.id).map((e) => ({ e, nuit: n.nuit.nuit })))
  const manuelles = f.nuits.filter((n) => n.nuit.manuelle).map((n) => ({ id: n.nuit.manuelle!.id, nuit: n.nuit.nuit }))
  const scinder = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await envoyer('POST', `/api/v1/associations/${a.id}/scinder`, { name: nom.trim(), kind, evenements: evts, nuits, alias })
      if (r) { setErreur(r); return }
      setNom(''); setEvts([]); setNuits([]); setAlias([])
      onClose()
    } finally { setEnvoi(false) }
  }
  return (
    <Modal open={open} onClose={onClose} ariaLabel={`Scinder ${a.name}`} width={620}>
      <div className="asso-dialogue">
        <h3>Scinder « {a.name} »</h3>
        <p className="lc-hint">Les soirées cochées passent à une nouvelle association ; les autres restent à « {a.name} ». « Annuler » remet tout comme avant.</p>
        <Field label="Nom de la nouvelle association">
          <TextInput value={nom} onChange={setNom} maxLength={80} ariaLabel="Nom de la nouvelle association" />
        </Field>
        <Select label="Type" value={kind} options={OPTIONS_KIND} onChange={(v) => setKind(v as KindAssociation)} />
        <fieldset className="asso-cases">
          <legend>Soirées à déplacer</legend>
          {propres.length === 0 && manuelles.length === 0 ? <p className="lc-hint">Aucune soirée à déplacer.</p> : null}
          {propres.map(({ e, nuit }) => (
            <Checkbox key={e.id} checked={evts.includes(e.id)} onChange={() => setEvts(bascule(evts, e.id))}
              label={`${e.name} · ${nuitLongue(nuit)} · ${libelleSource(e.source)} · ${nombre(e.vendus)} billets`} />
          ))}
          {manuelles.map((m) => (
            <Checkbox key={m.id} checked={nuits.includes(m.id)} onChange={() => setNuits(bascule(nuits, m.id))}
              label={`Nuit marquée à la main · ${nuitLongue(m.nuit)}`} />
          ))}
        </fieldset>
        {f.alias.length > 0 ? (
          <fieldset className="asso-cases">
            <legend>Alias à emporter</legend>
            {f.alias.map((x) => <Checkbox key={x.id} checked={alias.includes(x.id)} onChange={() => setAlias(bascule(alias, x.id))} label={x.label} />)}
          </fieldset>
        ) : null}
        {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
        <div className="asso-dialogue__pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose}>Annuler</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={envoi || !nom.trim() || evts.length + nuits.length === 0} onClick={() => { void scinder() }}>
            {envoi ? 'Scission…' : `Créer « ${nom.trim() || '…'} » avec ${compte(evts.length + nuits.length, 'soirée', 'soirées')}`}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/* ═══════════════════════════════════════ prochaine soirée ═══ */

const ETATS_PROCHAINE: Record<string, string> = {
  RATTACHEE: 'rattachée à la soirée du club',
  SEPAREE: 'gardée séparée de la soirée du club',
  A_RATTACHER: 'pas encore rattachée à une soirée du club',
  CLUB: 'vendue sur Shotgun',
}

function ProchaineSoiree({ f }: { f: Fiche }) {
  const menuSoiree = useMenuSoiree()
  const p = f.prochaine
  if (!p) return null
  const aRattacher = p.evenements.some((e) => e.etat === 'A_RATTACHER')
  return (
    <section className={`lc-panel gd-panel asso-prochaine${aRattacher ? ' asso-prochaine--attention' : ''}`} aria-label="Prochaine soirée">
      <div className="lc-panel__body asso-prochaine__corps">
        <CalendarClock size={22} aria-hidden="true" className="asso-prochaine__icone" />
        <div>
          <p className="asso-prochaine__titre">Prochaine soirée : <strong>{nuitLongue(p.nuit)}</strong> · {compte(p.vendus, 'billet vendu', 'billets vendus')} à ce jour</p>
          <ul className="asso-liste-simple">
            {p.evenements.map((e) => (
              <li key={e.event_id}>
                <Link href={`/soirees/${e.event_id}`} {...menuSoiree.soiree({ id: e.event_id, name: e.name })}>{e.name}</Link> — {ETATS_PROCHAINE[e.etat]}
                {e.parent_name ? ` (« ${e.parent_name} »${e.method === 'AUTO' ? ', automatiquement' : e.method === 'MANUEL' ? ', à la main' : ''})` : ''}
              </li>
            ))}
          </ul>
          {aRattacher || f.alerte_prochaine ? (
            <p className="lc-hint">
              {f.alerte_prochaine?.genre === 'SEULE'
                ? phraseSeuleUne(f.attach_mode)
                : 'Tant qu’elle n’est pas rattachée, elle compte comme une soirée à part.'}{' '}
              {/* L'alerte « avant la nuit » : envoyée, ou prévue (20/10/2026). */}
              {f.alerte_prochaine && ['ENVOYEE', 'PREVUE', 'SANS_DESTINATAIRE'].includes(f.alerte_prochaine.etat) ? `${f.alerte_prochaine.phrase} ` : ''}
              <Link href={`${CHEMIN_LISTE}?vue=${VUE_A_DECIDER}${f.alerte_prochaine ? `&cas=${encodeURIComponent(f.alerte_prochaine.cle)}` : ''}`}>Décider dans « À décider »</Link>.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/* ═══════════════════════════════════════ chiffres ═══ */

function Chiffres({ f }: { f: Fiche }) {
  const i = f.indicateurs
  return (
    <section className="asso-kpis" aria-label="Ses chiffres">
      <div className="asso-kpi"><span className="asso-kpi__libelle">Soirées</span><span className="asso-kpi__valeur">{nombre(i.nuits)}</span>
        <span className="asso-kpi__aide">{i.derniere_nuit ? `dernière : ${nuitLongue(i.derniere_nuit)}` : 'aucune passée'}</span></div>
      <div className="asso-kpi"><span className="asso-kpi__libelle">Billets vendus</span><span className="asso-kpi__valeur">{nombre(i.vendus_total)}</span>
        <span className="asso-kpi__aide">{i.moyenne_vendus !== null ? `${nombre(i.moyenne_vendus)} par soirée` : '—'}</span></div>
      <div className="asso-kpi"><span className="asso-kpi__libelle">Entrées</span><span className="asso-kpi__valeur">{nombre(i.entrees_total)}</span>
        <span className="asso-kpi__aide">{aideEntrees(i)}</span></div>
      <div className="asso-kpi"><span className="asso-kpi__libelle">Absents</span><span className="asso-kpi__valeur">{pourcent(i.no_show_pct)}</span>
        <span className="asso-kpi__aide">billets vendus jamais scannés</span></div>
      <div className="asso-kpi"><span className="asso-kpi__libelle">Tendance</span>
        <span className="asso-kpi__valeur asso-kpi__valeur--texte">{texteTendance(i.tendance)}</span>
        <span className="asso-kpi__aide">{i.tendance ? `dernière soirée face aux ${i.tendance.serie.length - 1} d’avant` : 'il faut deux soirées passées'}</span></div>
    </section>
  )
}

function Tendances({ f }: { f: Fiche }) {
  // Les soirées passées avec billets, la plus ancienne d'abord : un point par soirée.
  const passees = f.nuits.filter((n) => n.indicateurs.terminee && (n.indicateurs.vendus ?? 0) > 0)
    .sort((x, y) => x.nuit.nuit.localeCompare(y.nuit.nuit))
  if (passees.length === 0) return null
  const colonnes = passees.map((n) => nuitCourte(n.nuit.nuit))
  const details = passees.map((n) => detailNuit(n.nuit.nuit))
  return (
    <Bloc id="tendances" titre="Tendances">
      <div className="asso-graphes">
        <figure className="asso-figure">
          <figcaption>Billets vendus et entrées, soirée par soirée</figcaption>
          <MiniGraphe kind="barres" colonnes={colonnes} details={details} ariaLabel="Billets vendus et entrées par soirée"
            series={[
              { nom: 'Vendus', couleur: COULEURS_SERIES[0], valeurs: passees.map((n) => n.indicateurs.vendus) },
              { nom: 'Entrées', couleur: COULEURS_SERIES[1], valeurs: passees.map((n) => n.indicateurs.entrees) },
            ]} />
        </figure>
        <figure className="asso-figure">
          <figcaption>Absents (billets jamais scannés), en %</figcaption>
          <MiniGraphe kind="ligne" pourcentage colonnes={colonnes} details={details} ariaLabel="Part d’absents par soirée" format={(n) => pourcent(n)}
            series={[{ nom: 'Absents', couleur: COULEURS_SERIES[3], valeurs: passees.map((n) => n.indicateurs.no_show_pct) }]} />
        </figure>
        <figure className="asso-figure">
          <figcaption>Premières ventes, en jours avant la soirée</figcaption>
          <MiniGraphe kind="ligne" colonnes={colonnes} details={details} ariaLabel="Avance des ventes par soirée" format={(n) => joursAvant(n)}
            series={[
              { nom: 'Première vente', couleur: COULEURS_SERIES[0], valeurs: passees.map((n) => n.indicateurs.ventes_avant_jours) },
              { nom: 'Moitié vendue', couleur: COULEURS_SERIES[2], valeurs: passees.map((n) => n.indicateurs.mi_ventes_avant_jours) },
            ]} />
        </figure>
        <figure className="asso-figure">
          <figcaption>Formules vendues, toutes soirées (pour le stock du bar)</figcaption>
          <BarreMix mix={f.indicateurs.mix} />
        </figure>
      </div>
    </Bloc>
  )
}

/* ═══════════════════════════════════════ ses soirées ═══ */

function SesSoirees({ f, autres, vivante }: { f: Fiche; autres: Association[]; vivante: boolean }) {
  const gerer = f.droits.gerer
  const [marquer, setMarquer] = useState(false)
  // Le titre dit le même nombre que le chiffre « Soirées » : une page vide
  // passée (« SOIREE MEDECINE 2/2 », 0 billet) est montrée, pas comptée — et c'est dit.
  const comptees = f.nuits.filter((n) => n.comptee).length
  const vides = f.nuits.length - comptees
  return (
    <Bloc id="soirees" titre={`Ses soirées (${comptees})`}
      actions={gerer && vivante ? <button type="button" className="lc-btn lc-btn--sm" onClick={() => setMarquer((x) => !x)}><Plus size={14} aria-hidden="true" /> Marquer une nuit à la main</button> : null}>
      {marquer ? <MarquerNuit f={f} onFini={() => setMarquer(false)} /> : null}
      {vides > 0 ? (
        <p className="lc-hint asso-bloc__texte">
          Plus {compte(vides, 'soirée sans aucun billet, montrée mais non comptée', 'soirées sans aucun billet, montrées mais non comptées')}{' '}
          (une page créée puis jamais vendue{f.nuits.some((n) => !n.comptee && n.indicateurs.manuelle) ? ', ou une nuit marquée à la main que le réglage ne compte pas' : ''}).
        </p>
      ) : null}
      {f.nuits.length === 0 ? (
        <p className="lc-hint">
          Aucune soirée ne lui est liée pour l’instant. Ses soirées arrivent quand leur nom la désigne (ses alias, plus bas),
          ou quand vous les liez depuis « À décider ».
        </p>
      ) : (
        <ul className="asso-nuits">
          {f.nuits.map((n) => <LigneNuit key={n.nuit.nuit} n={n} f={f} autres={autres} />)}
        </ul>
      )}
    </Bloc>
  )
}

function LigneNuit({ n, f, autres }: { n: NuitDeLaFiche; f: Fiche; autres: Association[] }) {
  const i = n.indicateurs
  const { envoyer } = useGestes()
  const m = n.nuit.manuelle
  return (
    <li className="asso-nuit">
      <div className="asso-nuit__tete">
        <strong>{nuitLongue(n.nuit.nuit)}</strong>
        {!i.terminee ? <span className="lc-pill lc-pill--ok">À venir</span> : null}
        {!n.comptee ? <span className="lc-pill lc-pill--idle" title="Aucun billet : elle n’entre dans aucun chiffre">Non comptée</span> : null}
        {i.groupes.length > 0 ? <span className="lc-hint">avec {i.groupes.join(', ')}</span> : null}
        {i.porte ? <span className="lc-pill lc-pill--idle" title="Billets saisis après l’ouverture">Vendue sur place</span> : null}
      </div>
      <dl className="asso-nuit__chiffres">
        <div><dt>Vendus</dt><dd>{nombre(i.vendus)}</dd></div>
        <div><dt>Entrées</dt><dd>{nombre(i.entrees)}{i.entrees_source === 'MANUEL' ? ' (saisies)' : i.entrees_source === 'COMPTA' ? ' (Comptabilité)' : ''}</dd></div>
        <div><dt>Absents</dt><dd>{pourcent(i.no_show_pct)}</dd></div>
        <div><dt>Ventes</dt><dd>{joursAvant(i.ventes_avant_jours)}</dd></div>
      </dl>
      {i.mix ? <BarreMix mix={i.mix} /> : null}
      <ul className="asso-nuit__evenements">
        {n.nuit.evenements.map((e) => <LigneEvenement key={e.id} e={e} f={f} autres={autres} />)}
        {m ? (
          <li className="asso-nuit__evenement">
            <span>Nuit marquée à la main{m.note ? ` — « ${m.note} »` : ''}
              {m.nuit_partagee ? <span className="lc-hint"> · d’autres soirées ont vendu cette nuit-là : les entrées de la Comptabilité ne lui sont pas comptées</span> : null}
            </span>
            {f.droits.gerer ? (
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { void envoyer('POST', `/api/v1/associations/nuits/${m.id}/retirer`, {}) }}>Retirer</button>
            ) : null}
          </li>
        ) : null}
      </ul>
    </li>
  )
}

function LigneEvenement({ e, f, autres }: { e: EvenementDeNuit; f: Fiche; autres: Association[] }) {
  const { envoyer } = useGestes()
  const menuSoiree = useMenuSoiree()
  const [ouvert, setOuvert] = useState(false)
  const [choix, setChoix] = useState('')
  const [motif, setMotif] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  // Vue à travers un groupement : la soirée appartient à un membre — elle se corrige sur sa fiche.
  const duMembre = f.association.kind === 'GROUPEMENT' && e.lien.group_id === f.association.id
  const agir = async (pas: boolean) => {
    setEnvoi(true); setErreur(null)
    try {
      const r = pas
        ? await envoyer('POST', '/api/v1/associations/liens/pas-association', { evenements: [e.id], motif: motif.trim() || null })
        : await envoyer('POST', '/api/v1/associations/liens/lier', { evenements: [e.id], association_id: choix, motif: motif.trim() || null })
      if (r) setErreur(r); else setOuvert(false)
    } finally { setEnvoi(false) }
  }
  return (
    <li className="asso-nuit__evenement">
      <span>
        <Link href={`/soirees/${e.id}`} {...menuSoiree.soiree(e)}>{e.name}</Link>
        <span className="lc-hint"> · {libelleSource(e.source)} · {nombre(e.vendus)} vendus · {texteLien(e.lien)}</span>
      </span>
      {f.droits.gerer && !duMembre && !ouvert ? (
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setOuvert(true)}>Corriger…</button>
      ) : null}
      {ouvert ? (
        <div className="asso-carte__form asso-carte__form--ligne">
          <ChoixAssociation label="La lier plutôt à" associations={autres} value={choix} onChange={setChoix} sauf={f.association.id} />
          <Field label="Pourquoi" helper={`Quelques mots (${f.motif_min} caractères au moins) pour la lier ailleurs ; facultatif pour « pas une association ».`}>
            <TextInput value={motif} onChange={setMotif} maxLength={300} ariaLabel="Motif" />
          </Field>
          {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
          <div className="asso-carte__gestes">
            <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={envoi || !choix || motif.trim().length < f.motif_min} onClick={() => { void agir(false) }}>Lier ailleurs</button>
            <button type="button" className="lc-btn lc-btn--sm" disabled={envoi} onClick={() => { void agir(true) }}>Pas une association</button>
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setOuvert(false); setErreur(null) }}>Fermer</button>
          </div>
        </div>
      ) : null}
    </li>
  )
}

function MarquerNuit({ f, onFini }: { f: Fiche; onFini: () => void }) {
  const { envoyer } = useGestes()
  const [nuit, setNuit] = useState('')
  const [entrees, setEntrees] = useState('')
  const [note, setNote] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const entreesInvalides = entrees.trim() !== '' && !(Number.isFinite(Number(entrees)) && Number(entrees) >= 0)
  const marquer = async () => {
    setEnvoi(true); setErreur(null)
    try {
      const r = await envoyer('POST', `/api/v1/associations/${f.association.id}/nuits`, {
        nuit, entrees: entrees.trim() === '' ? null : Math.round(Number(entrees)), note: note.trim() || null,
      })
      if (r) setErreur(r); else onFini()
    } finally { setEnvoi(false) }
  }
  return (
    <div className="asso-carte__form asso-marquer">
      <p className="lc-hint" style={{ margin: 0 }}>
        Pour une nuit sans soirée nommée (« 803 × 6 € » vendus sur place) : elle compte parmi ses soirées. Ses billets
        restent ceux du club ; les entrées sont celles que vous saisissez, sinon celles de la Comptabilité quand la nuit n’a eu qu’elle.
      </p>
      <Field label="Nuit"><TextInput type="date" value={nuit} onChange={setNuit} ariaLabel="Date de la nuit" /></Field>
      <Field label="Entrées (facultatif)"><NumberInput value={entrees} onChange={setEntrees} min={0} max={100000} ariaLabel="Entrées de la nuit" /></Field>
      <Field label="Note (facultatif)"><TextInput value={note} onChange={setNote} maxLength={300} ariaLabel="Note de la nuit" /></Field>
      {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
      <div className="asso-carte__gestes">
        <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={envoi || !/^\d{4}-\d{2}-\d{2}$/.test(nuit) || entreesInvalides} onClick={() => { void marquer() }}>Marquer la nuit</button>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={onFini}>Fermer</button>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════ opérations ═══ */

function OperationsBloc({ f }: { f: Fiche }) {
  const suivies = f.operations.filter((o) => !o.avant_suivi).sort((a, b) => b.nuit.localeCompare(a.nuit))
  const avant = f.operations.length - suivies.length
  if (f.operations.length === 0) return null
  return (
    <Bloc id="operations" titre="Ses nuits côté exploitation">
      {suivies.length === 0 ? (
        <p className="lc-hint">Agents, staff et incidents ne sont relevés que depuis le 30/07/2026 : aucune de ses soirées n’a eu lieu depuis.</p>
      ) : (
        <>
          {/* Défile dans sa zone : à 390 px, le panneau (overflow: hidden) coupait les incidents. */}
          <div className="asso-defile" tabIndex={0} role="region" aria-label="Agents, staff et incidents par nuit">
          <table className="lc-table asso-table asso-table--compacte">
            <thead><tr><th scope="col">Nuit</th><th scope="col" className="asso-num">Agents (pic)</th><th scope="col" className="asso-num">Staff (pic)</th><th scope="col" className="asso-num">Incidents</th><th scope="col" className="asso-num">Dont critiques</th></tr></thead>
            <tbody>
              {suivies.map((o) => (
                <tr key={o.nuit}>
                  <td>{nuitLongue(o.nuit)}</td>
                  <td className="asso-num">{nombre(o.agents_pic)}</td>
                  <td className="asso-num">{nombre(o.staff_pic)}</td>
                  <td className="asso-num">{nombre(o.incidents)}</td>
                  <td className="asso-num">{nombre(o.incidents_critiques)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p className="lc-hint">Relevés de la Main Courante sur la soirée de la nuit (pour une nuit marquée à la main : toutes les soirées de cette nuit, annulées comprises). « — » : aucun relevé pour cette nuit.{avant > 0 ? ` ${compte(avant, 'soirée plus ancienne', 'soirées plus anciennes')} que le 30/07/2026 n’ont pas de relevé.` : ''}</p>
        </>
      )}
    </Bloc>
  )
}

/* ═══════════════════════════════════════ l'argent ═══ */

function ArgentBloc({ f }: { f: Fiche }) {
  if (f.argent_masque) return null
  const g = f.argent
  if (!g) return null
  const nuits = [...g.nuits].sort((a, b) => b.nuit.localeCompare(a.nuit))
  return (
    <Bloc id="argent" titre="L’argent de ses soirées">
      <div className="asso-kpis asso-kpis--argent">
        <div className="asso-kpi"><span className="asso-kpi__libelle">Valeur faciale (repère)</span><span className="asso-kpi__valeur">{euros(g.face_cents)}</span>
          <span className="asso-kpi__aide">{g.nuits_connues > 0 ? `sur ${compte(g.nuits_connues, 'soirée', 'soirées')} dont la part est connue` : 'aucune soirée dont la part est connue'}</span></div>
        <div className="asso-kpi"><span className="asso-kpi__libelle">Part de l’association</span><span className="asso-kpi__valeur">{euros(g.share_cents)}</span></div>
        <div className="asso-kpi"><span className="asso-kpi__libelle">Ce qui reste</span><span className="asso-kpi__valeur">{euros(g.kept_cents)}</span>
          <span className="asso-kpi__aide">valeur faciale moins la part de l’association</span></div>
        {g.fees_cents !== null ? (
          <div className="asso-kpi"><span className="asso-kpi__libelle">Frais Stripe estimés</span><span className="asso-kpi__valeur">{euros(g.fees_cents)}</span></div>
        ) : null}
        {g.face_part_inconnue_cents !== null ? (
          <div className="asso-kpi"><span className="asso-kpi__libelle">Valeur faciale, part inconnue</span><span className="asso-kpi__valeur">{euros(g.face_part_inconnue_cents)}</span>
            <span className="asso-kpi__aide">{compte(g.nuits_inconnues, 'soirée', 'soirées')} : montrée à part, jamais additionnée</span></div>
        ) : null}
      </div>
      {/* Défile dans sa zone : à 390 px, « Part de l’association » et « Ce qui reste » sortaient du panneau. */}
      <div className="asso-defile" tabIndex={0} role="region" aria-label="L’argent, nuit par nuit">
      <table className="lc-table asso-table asso-table--compacte">
        <thead><tr><th scope="col">Nuit</th><th scope="col" className="asso-num">Valeur faciale (repère)</th><th scope="col" className="asso-num">Part de l’association</th><th scope="col" className="asso-num">Ce qui reste</th>{g.fees_cents !== null ? <th scope="col" className="asso-num">Frais estimés</th> : null}</tr></thead>
        <tbody>
          {nuits.map((n) => (
            <tr key={n.nuit}>
              <td>{nuitLongue(n.nuit)}{n.part_par_billet_cents !== null ? <span className="lc-hint"> · {euros(n.part_par_billet_cents, true)} par billet{n.part_origine === 'DEFAUT' ? ' (par défaut)' : ''}</span> : null}</td>
              <td className="asso-num">{n.face_cents === null ? '—' : euros(n.face_cents)}</td>
              <td className="asso-num" title={n.raison ?? undefined}>{euros(n.share_cents)}</td>
              <td className="asso-num">{euros(n.kept_cents)}</td>
              {g.fees_cents !== null ? <td className="asso-num">{n.inconnu ? '—' : euros(n.fees_cents)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <ul className="asso-notes">
        {g.notes.map((n) => <li key={n}>{n}</li>)}
        <li>La part par billet se règle par événement dans Comptabilité › Règles (2 € par défaut).</li>
      </ul>
    </Bloc>
  )
}

/* ═══════════════════════════════════════ identité, note, alias ═══ */

function Identite({ f, autres }: { f: Fiche; autres: Association[] }) {
  const a = f.association
  const gerer = f.droits.gerer && a.status !== 'REJECTED' && !a.merged_into_id
  // La fiche se relit après CHAQUE geste (un alias ajouté, une soirée liée) :
  // le formulaire ne revient aux valeurs du serveur que si elles ont changé,
  // sinon une note en cours de saisie disparaissait au premier alias ajouté.
  const cle = JSON.stringify(formDe(a))
  const serveur = useMemo(() => JSON.parse(cle) as FormIdentite, [cle])
  const [v, setV] = useState<FormIdentite>(serveur)
  useEffect(() => setV(serveur), [serveur])
  const correctif = correctifIdentite(serveur, v)
  const nbChanges = Object.keys(correctif).length
  useSaveBar({
    dirty: gerer && nbChanges > 0,
    count: nbChanges,
    saveLabel: 'Enregistrer l’identité',
    invalid: v.name.trim() === '' ? 'Le nom ne peut pas être vide.' : null,
    onSave: async () => {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('PUT', `/api/v1/associations/${a.id}`, correctif)
      if (r.status !== 200 || !r.data.ok) return r.data.message ?? 'Enregistrement impossible.'
      signalerAssociations()
      return true
    },
    onCancel: () => setV(serveur),
    successMessage: 'Identité enregistrée. Elle se rétablit depuis l’historique, en bas de la fiche.',
  })
  return (
    <Bloc id="identite" titre="Identité, note et alias">
      <div className="asso-identite">
        <div className="asso-identite__champs">
          <Field label="Nom"><TextInput value={v.name} onChange={(x) => setV({ ...v, name: x })} maxLength={80} disabled={!gerer} ariaLabel="Nom" /></Field>
          <Select label="Type" value={v.kind} options={OPTIONS_KIND} onChange={(x) => { if (gerer) setV({ ...v, kind: x as KindAssociation }) }} />
          <Field label="École"><TextInput value={v.school} onChange={(x) => setV({ ...v, school: x })} maxLength={160} disabled={!gerer} ariaLabel="École" /></Field>
          {v.kind !== 'GROUPEMENT' ? (
            <Select label="Membre d’un groupement" value={v.member_of_id} options={optionsGroupements([...autres, ...(a.member_of_id && !autres.some((x) => x.id === a.member_of_id) ? [{ id: a.member_of_id, name: a.member_of_name ?? '…', kind: 'GROUPEMENT' as const, status: 'ACTIVE' as const }] : [])], a.id)}
              onChange={(x) => { if (gerer) setV({ ...v, member_of_id: x }) }} />
          ) : null}
          <Field label="Rôle du contact (facultatif)" helper="Le rôle, pas la personne : « Président·e du BDE, via Instagram ».">
            <TextInput value={v.contact_role} onChange={(x) => setV({ ...v, contact_role: x })} maxLength={160} disabled={!gerer} ariaLabel="Rôle du contact" />
          </Field>
          <Field label="Note" helper="Pas de numéro ni d’e-mail personnel : la note est lue par toute personne qui consulte les associations.">
            <textarea className="lc-input asso-note" value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} maxLength={2000} disabled={!gerer} aria-label="Note" rows={4} />
          </Field>
        </div>
        <Alias f={f} />
      </div>
    </Bloc>
  )
}

function Alias({ f }: { f: Fiche }) {
  const { envoyer } = useGestes()
  const [nouvel, setNouvel] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const gerer = f.droits.gerer && (f.association.status === 'ACTIVE' || f.association.status === 'SUGGESTED')
  const ajouter = async () => {
    setErreur(null)
    const r = await envoyer('POST', `/api/v1/associations/${f.association.id}/alias`, { alias: nouvel.trim() })
    if (r) setErreur(r); else setNouvel('')
  }
  return (
    <div className="asso-alias">
      <h3 className="asso-sous-titre">Alias</h3>
      <p className="lc-hint">Les mots du nom des soirées qui la désignent. Gestion ne lit jamais l’adresse Pretix ni le libellé bancaire, et refuse « étudiant », « campus » ou « soirée » à eux seuls.</p>
      {f.alias.length === 0 ? <p className="lc-hint">Aucun alias : aucune soirée ne la reconnaît par son nom.</p> : (
        <ul className="asso-puces">
          {f.alias.map((x) => (
            <li key={x.id} className="asso-puce">
              {x.label}
              {gerer ? (
                <button type="button" className="asso-puce__retirer" aria-label={`Retirer l’alias ${x.label}`}
                  onClick={() => { void envoyer('POST', `/api/v1/associations/alias/${x.id}/retirer`, {}) }}>
                  <X size={12} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {gerer ? (
        <div className="asso-alias__ajout">
          <TextInput value={nouvel} onChange={setNouvel} maxLength={60} placeholder="POST PARTIEL MEDECINE" ariaLabel="Nouvel alias" />
          <button type="button" className="lc-btn lc-btn--sm" disabled={!nouvel.trim()} onClick={() => { void ajouter() }}><Plus size={14} aria-hidden="true" /> Ajouter</button>
        </div>
      ) : null}
      {erreur ? <p className="lc-helper lc-helper--error" role="alert">{erreur}</p> : null}
    </div>
  )
}

/* ═══════════════════════════════════════ historique ═══ */

function Historique({ f }: { f: Fiche }) {
  const { annuler } = useGestes()
  const confirmer = useConfirm()
  if (f.historique.length === 0) return null
  const fmt = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  return (
    <Bloc id="historique" titre="Historique">
      <ol className="asso-historique">
        {f.historique.map((g) => (
          <li key={g.id} className={g.undone ? 'asso-historique__annule' : undefined}>
            <span className="asso-historique__quand">{fmt(g.at)}</span>
            <span>
              {g.summary}{g.reason ? ` — « ${g.reason} »` : ''}
              <span className="lc-hint"> · {g.actor_name ?? 'Gestion (automatique)'}{g.undone ? ' · annulé' : ''}</span>
            </span>
            {f.droits.gerer && !g.undone && g.kind !== 'ANNULER' ? (
              <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={async () => {
                const ok = await confirmer({ title: 'Annuler ce geste ?', message: `« ${g.summary} » : Gestion rétablit ce qu’il y avait avant, sauf ce qui a été retouché depuis.`, confirmLabel: 'Annuler le geste', cancelLabel: 'Garder' })
                if (ok) await annuler(g.id)
              }}><Undo2 size={14} aria-hidden="true" /> Annuler</button>
            ) : null}
          </li>
        ))}
      </ol>
    </Bloc>
  )
}
